import {
    DataType,
    DataTypeOperator,
    FilterRule
} from "crqlar-react-common/dist/models/audience/filter.model";
import {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import {
    getCountryISO2Cdes,
    getCountryNameForISO2
} from "crqlar-react-common/dist/utils/country.utils";
import {
    getLanguageLabel,
    getLanuageISO2Codes,
    getNameForLanguage,
    handleExceptionRemoval
} from "crqlar-react-common/dist/utils/translations.util";

export const FILTER_OPERATORS_WITHOUT_VALUE = new Set<DataTypeOperator>([
    DataTypeOperator.EXISTS,
    DataTypeOperator.NOT_EXISTS,
    DataTypeOperator.TRUE,
    DataTypeOperator.FALSE,
    DataTypeOperator.IN_THIS_YEAR,
    DataTypeOperator.NOT_IN_THIS_YEAR
]);

export const FILTER_TWO_VALUE_OPERATORS = new Set<DataTypeOperator>([
    DataTypeOperator.BETWEEN,
    DataTypeOperator.NOT_BETWEEN
]);

const FILTER_SINGLE_VALUE_OPERATORS = new Set<DataTypeOperator>([
    DataTypeOperator.EQUAL,
    DataTypeOperator.NOT_EQUAL,
    DataTypeOperator.MORE_THAN,
    DataTypeOperator.MORE_THAN_EQUAL,
    DataTypeOperator.LESS_THAN,
    DataTypeOperator.LESS_THAN_EQUAL,
    DataTypeOperator.CONTAINS,
    DataTypeOperator.DOES_NOT_CONTAIN,
    DataTypeOperator.SIMILAR_TO,
    DataTypeOperator.STARTS_WITH,
    DataTypeOperator.ENDS_WITH
]);

const FILTER_MULTI_VALUE_OPERATORS = new Set<DataTypeOperator>([
    DataTypeOperator.IN,
    DataTypeOperator.NOT_IN
]);

const SUPPORTED_TWO_VALUE_TYPES = new Set<DataType>([
    DataType.NUMBER,
    DataType.DECIMAL,
    DataType.LOCAL_DATE,
    DataType.TIMESTAMP
]);

const LOOKUP_ID_TYPES = new Set([
    'resource',
    'category',
    'service_category',
    'profile',
    'user',
    'sector',
    'service',
    'integration_config',
    'campaign'
]);

export interface IdTypeValueSource {
    codes: () => string[]
    labelFor: (code: string) => string
    displaySelection?: (values: string[]) => string[]
    // reconcile linked legacy codes (e.g. language 'de' ↔ 'D') when the selection changes
    normalizeSelection?: (previous: string[], next: string[]) => string[]
}

const collapseLinkedLanguageCodes = (values: string[]) => {
    const normalized = new Set(values.map(value => value.toLowerCase()));
    return values.filter(value => !(
        (value === 'D' && normalized.has('de'))
        || (value === 'E' && normalized.has('en'))
    ));
};

// idType-backed TEXT options are advertised by the backend with IN/NOT_IN only and expect
// the client to supply the value list; entries here are the idTypes resolvable without a
// backend lookup. Options with an unlisted idType stay hidden from the field picker.
const ID_TYPE_VALUE_SOURCES: Record<string, IdTypeValueSource> = {
    country_code_iso_3166_alpha_2: {
        codes: getCountryISO2Cdes,
        labelFor: code => getCountryNameForISO2(code) || code
    },
    language_code_iso_639_1: {
        codes: getLanuageISO2Codes,
        labelFor: code => getLanguageLabel(code, getNameForLanguage) || code,
        displaySelection: collapseLinkedLanguageCodes,
        normalizeSelection: handleExceptionRemoval
    }
};

export const getIdTypeValueSource = (option?: FilterOption) => (
    option?.idType ? ID_TYPE_VALUE_SOURCES[option.idType] : undefined
);

export const hasIdTypeValueEditor = (option?: FilterOption) => (
    option?.dataType === DataType.TEXT
    && !!option.idType
    && (!!getIdTypeValueSource(option) || LOOKUP_ID_TYPES.has(option.idType))
);

export const getSupportedFilterOperators = (option: FilterOption) => {
    if (option.dataType === DataType.COMPLEX || option.dataType === DataType.FUNCTIONS) {
        return [];
    }

    return (option.allowedOperators ?? []).filter(operator => (
        FILTER_OPERATORS_WITHOUT_VALUE.has(operator)
        || FILTER_SINGLE_VALUE_OPERATORS.has(operator)
        || (FILTER_TWO_VALUE_OPERATORS.has(operator) && SUPPORTED_TWO_VALUE_TYPES.has(option.dataType))
        || (FILTER_MULTI_VALUE_OPERATORS.has(operator)
            && (option.dataType === DataType.ENUM || hasIdTypeValueEditor(option)))
    ));
};

export const getFilterRuleValues = (
    rule: FilterRule
): Array<string | number | Date | undefined> => (
    rule.textValues
    ?? rule.longValues
    ?? rule.doubleValues
    ?? rule.localDateValues
    ?? rule.timestampValues
    ?? []
);

const isOrderedPair = (lower: string | number | Date, upper: string | number | Date) => {
    if (lower instanceof Date && upper instanceof Date) return lower.getTime() <= upper.getTime();
    if (typeof lower === 'number' && typeof upper === 'number') return lower <= upper;
    return String(lower) <= String(upper);
};

export const isCompleteFilterRule = (rule: FilterRule, option?: FilterOption) => {
    if (!rule.field || !rule.operator || !option) return false;
    if (!getSupportedFilterOperators(option).includes(rule.operator)) return false;
    if (FILTER_OPERATORS_WITHOUT_VALUE.has(rule.operator)) return true;

    const values = getFilterRuleValues(rule);
    const requiredValueCount = FILTER_TWO_VALUE_OPERATORS.has(rule.operator) ? 2 : 1;
    if (values.length < requiredValueCount) return false;
    if (!values.slice(0, requiredValueCount).every(value => value !== undefined && value !== '')) return false;

    return !FILTER_TWO_VALUE_OPERATORS.has(rule.operator)
        || isOrderedPair(
            values[0] as string | number | Date,
            values[1] as string | number | Date
        );
};

export const changeFilterRuleOperator = (rule: FilterRule, operator: DataTypeOperator): FilterRule => {
    if (FILTER_OPERATORS_WITHOUT_VALUE.has(operator)) {
        return {
            field: rule.field,
            operator,
            isExclusive: rule.isExclusive,
            isDeactivated: rule.isDeactivated
        };
    }

    const valueLimit = FILTER_TWO_VALUE_OPERATORS.has(operator) ? 2 : 1;
    const truncate = <T, >(values?: T[]) => values?.slice(0, valueLimit);

    return {
        ...rule,
        operator,
        textValues: FILTER_MULTI_VALUE_OPERATORS.has(operator)
            ? rule.textValues
            : rule.textValues?.slice(0, valueLimit),
        longValues: truncate(rule.longValues),
        doubleValues: truncate(rule.doubleValues),
        localDateValues: truncate(rule.localDateValues),
        timestampValues: truncate(rule.timestampValues)
    };
};
